/* eslint-disable fp/no-mutation */

/**
 * Edit Credential Modal
 * Modal for editing credential details only
 * Verification is handled separately
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Check as SubmitIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as UploadFileIcon,
  Close as ClearIcon,
} from '@mui/icons-material'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import type { RosterCredentialItem, CredentialFileData } from '../../types'
import { CREDENTIAL_TYPES } from '../../types'

dayjs.extend(utc)

interface EditCredentialModalProps {
  isOpen: boolean
  credential: RosterCredentialItem | null
  userID: string
  onClose: () => void
  onSuccess: () => void
  onFailure: (error: string) => void
}

export let EditCredentialModal = ({
  isOpen,
  credential,
  userID,
  onClose,
  onSuccess,
  onFailure,
}: EditCredentialModalProps) => {
  let [fileToUpload, setFileToUpload] = useState<
    CredentialFileData | undefined
  >()
  let [existingFile, setExistingFile] = useState<string | undefined>()

  let credentialType =
    credential?.type &&
    CREDENTIAL_TYPES.includes(credential?.type.toUpperCase() as any)
      ? credential?.type
      : 'Other'

  let updateCredentialImage = (data: {
    id: string
    userID?: string
    imageType: string
  }) =>
    axios.put<string>(`/api/v2/manage/credential/image`, data).then(pluckData)

  let validationSchema = z
    .object({
      credentialIDNumber: z
        .string()
        .nonempty('Credential ID Number is required'),
      issueDate: z.string(),
      expiration: z.string().nonempty('Expiration date is required'),
      type: z.string().nonempty('Credential Type is required'),
      customType: z.string(),
    })
    .refine(
      (data) => {
        if (data.expiration) {
          return dayjs(data.expiration, 'MM/DD/YYYY', true).isValid()
        }
        return true
      },
      {
        message: 'Expiration date is invalid',
        path: ['expiration'],
      },
    )
    .refine(
      (data) => {
        if (data.type === 'Other') {
          return z.string(data.customType).nonempty()
        }
        return true
      },
      {
        message: 'Custom Credential Type is required',
        path: ['customType'],
      },
    )

  let form = useForm({
    defaultValues: {
      credentialIDNumber: credential?.number || '',
      expiration: credential?.expiration
        ? dayjs.utc(credential.expiration).format('MM/DD/YYYY')
        : '',
      issueDate: credential?.issueDate,
      type: credentialType || '',
      customType: credentialType === 'Other' ? credential?.type : '',
    },
    validators: {
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      if (!credential?.id) return

      // Update credential
      await axios
        .put('/api/v2/manage/credential', {
          id: credential.id,
          userID,
          credentialIDNumber: value.credentialIDNumber,
          expiration: value.expiration,
          issueDate: value.issueDate,
          type:
            value.type === 'Other' ? { other: value.customType } : value.type,
        })
        .then(() => {
          if (fileToUpload) {
            updateCredentialImage({
              id: credential.id,
              userID,
              imageType: fileToUpload.extension,
            }).then((response) =>
              axios.put(response, fileToUpload?.file, {
                headers: {
                  'Cache-Control': 'max-age=2592000',
                  'content-type': fileToUpload?.type,
                },
              }),
            )
          }
        })
        .then(() => {
          form.reset()
          setFileToUpload(undefined)
          setExistingFile(undefined)
          onSuccess()
          onClose()
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Error updating credential:', err)
          onFailure('Failed to update credential')
        })
    },
  })

  // Update form when credential changes
  useEffect(() => {
    if (credential) {
      let newCredentialType =
        credential.type &&
        CREDENTIAL_TYPES.includes(credential.type.toUpperCase() as any)
          ? credential.type
          : 'Other'

      form.setFieldValue('type', newCredentialType || '')
      form.setFieldValue(
        'expiration',
        credential.expiration
          ? dayjs.utc(credential.expiration).format('MM/DD/YYYY')
          : '',
      )
      form.setFieldValue(
        'issueDate',
        credential.issueDate
          ? dayjs.utc(credential.issueDate).format('MM/DD/YYYY')
          : '',
      )
      form.setFieldValue('credentialIDNumber', credential.number || '')
      form.setFieldValue(
        'customType',
        newCredentialType === 'Other' ? credential.type || '' : '',
      )
      setExistingFile(credential.fileUrl || undefined)
      setFileToUpload(undefined)
    }
  }, [credential?.id])

  let typeValue = useStore(form.store, (state) => state.values.type)

  if (typeValue !== 'Other') {
    form.setFieldValue('customType', '')
  }

  // Handle file upload
  let handleCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0]
    if (!file) return

    let fileName = file.name
    let reader = new FileReader()
    reader.onloadend = () => {
      let blobData = new Blob([reader.result as ArrayBuffer], {
        type: file.type,
      })
      let fileExtension =
        file.type === 'application/pdf'
          ? 'pdf'
          : file.type === 'image/jpeg'
            ? 'jpg'
            : 'png'

      setFileToUpload({
        name: fileName,
        type: file.type,
        extension: fileExtension,
        file: blobData,
      })
    }
    reader.readAsArrayBuffer(file)
  }

  // Handle modal close
  let handleClose = () => {
    form.reset()
    setFileToUpload(undefined)
    setExistingFile(undefined)
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      aria-labelledby="edit-credential-dialog-title"
      aria-describedby="edit-credential-description"
      maxWidth="xs"
    >
      <Paper className="px-4 py-2">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          aria-label="Edit credential form"
        >
          <DialogTitle
            id="edit-credential-dialog-title"
            className="text-center"
          >
            Edit Credential
          </DialogTitle>
          <DialogContent className="flex flex-col gap-4 px-2 pb-6">
            <Typography
              id="edit-credential-description"
              variant="body2"
              sx={{ display: 'none' }}
            >
              Form to edit credential details. All fields except file upload are
              required.
            </Typography>
            <form.Field name="credentialIDNumber">
              {(field) => (
                <TextField
                  className="mt-2"
                  variant="outlined"
                  size="small"
                  label="Credential ID Number"
                  type="text"
                  fullWidth
                  required
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            </form.Field>

            <form.Field name="issueDate">
              {(field) => (
                <Box>
                  <Box className="flex justify-center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Issue Date"
                        value={
                          field.state.value
                            ? dayjs.utc(field.state.value)
                            : null
                        }
                        onChange={(val) =>
                          field.handleChange(
                            val ? dayjs.utc(val).format('MM/DD/YYYY') : '',
                          )
                        }
                        slotProps={{
                          textField: {
                            required: true,
                            error: shouldShowZodFieldError(field.state.meta),
                            helperText:
                              shouldShowZodFieldError(field.state.meta) &&
                              getZodValidationError(field.state.meta.errors),
                            variant: 'outlined',
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
              )}
            </form.Field>

            <form.Field name="expiration">
              {(field) => (
                <Box>
                  <Box className="flex justify-center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Expiration"
                        value={
                          field.state.value
                            ? dayjs.utc(field.state.value)
                            : null
                        }
                        onChange={(val) =>
                          field.handleChange(
                            val ? dayjs.utc(val).format('MM/DD/YYYY') : '',
                          )
                        }
                        slotProps={{
                          textField: {
                            required: true,
                            error: shouldShowZodFieldError(field.state.meta),
                            helperText:
                              shouldShowZodFieldError(field.state.meta) &&
                              getZodValidationError(field.state.meta.errors),
                            variant: 'outlined',
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
              )}
            </form.Field>

            <form.Field name="type">
              {(field) => (
                <FormControl
                  fullWidth
                  required
                  error={shouldShowZodFieldError(field.state.meta)}
                >
                  <InputLabel size="small">Credential Type</InputLabel>
                  <Select
                    value={field.state.value}
                    label="Credential Type"
                    onChange={(e) => field.handleChange(e.target.value)}
                    variant="outlined"
                    size="small"
                  >
                    {CREDENTIAL_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                  {shouldShowZodFieldError(field.state.meta) && (
                    <Typography
                      variant="caption"
                      color="error"
                      className="mt-1 ml-3.5"
                    >
                      {getZodValidationError(field.state.meta.errors)}
                    </Typography>
                  )}
                </FormControl>
              )}
            </form.Field>

            <form.Subscribe
              selector={(state) => [state.values.type]}
              children={([type]) =>
                type === 'Other' && (
                  <form.Field name="customType">
                    {(field) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Custom Credential Type"
                        type="text"
                        fullWidth
                        required
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value)}
                        error={shouldShowZodFieldError(field.state.meta)}
                        helperText={
                          shouldShowZodFieldError(field.state.meta) &&
                          getZodValidationError(field.state.meta.errors)
                        }
                      />
                    )}
                  </form.Field>
                )
              }
            />

            <Box>
              {existingFile && !fileToUpload && (
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                  }}
                  className="block mb-1"
                >
                  <UploadFileIcon
                    sx={{ fontSize: 14, mr: '2px', mb: '-2px' }}
                  />
                  {credential?.fileName || 'File not found'}
                </Typography>
              )}
              <label htmlFor="edit-credential-file-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  fullWidth
                  aria-describedby="edit-file-upload-helper"
                >
                  <input
                    id="edit-credential-file-upload"
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    type="file"
                    onChange={handleCapture}
                    aria-label={`${!credential?.id || !existingFile ? 'Select' : 'Overwrite'} credential file (optional, PDF, PNG, or JPG)`}
                    aria-describedby="edit-file-upload-helper"
                  />
                  {!credential?.id || !existingFile ? 'Select' : 'Overwrite'}{' '}
                  File
                </Button>
              </label>
              {fileToUpload && (
                <Box className="flex items-center justify-center gap-2 mt-2">
                  <Typography variant="subtitle1" className="flex items-center">
                    <UploadFileIcon
                      color="primary"
                      sx={{ mr: '2px', mb: '2px' }}
                    />
                    {fileToUpload.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setFileToUpload(undefined)}
                    aria-label="Clear file selection"
                    sx={{ color: 'text.secondary' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            <Typography
              id="edit-file-upload-helper"
              variant="caption"
              color="textSecondary"
            >
              * Allowed file types: png, jpg, pdf
              <br />* The existing credential file will only be overwritten upon
              submitting this form with a new image uploaded.
            </Typography>
          </DialogContent>

          <DialogActions
            disableSpacing={true}
            className="flex-col gap-2 pt-0 pb-6 mt-0"
          >
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    fullWidth
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SubmitIcon />
                      )
                    }
                    disabled={!canSubmit || isSubmitting}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={handleClose}
                    color="error"
                    variant="contained"
                    fullWidth
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            />
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}

export default EditCredentialModal
